import { apiClient } from './client'
import type { User } from '@/types/user'
import type { Auth0ConnectionsResponse } from '@/types/hospital'

export interface Auth0User {
  user_id: string
  email: string
  email_verified: boolean
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  created_at: string
  updated_at: string
  last_login?: string
  logins_count?: number
  blocked?: boolean
}

export interface Auth0UserResponse {
  linked: boolean
  stale?: boolean
  user: User
  auth0User?: Auth0User
  adminUrl?: string
  error?: string
}

export interface LinkToAuth0Request {
  auth0UserId: string
  createIfNotExists?: boolean
}

export interface CreateAuth0UserRequest {
  email?: string
  password?: string
  given_name?: string
  family_name?: string
  name?: string
  connection?: string
  email_verified?: boolean
  verify_email?: boolean
}

export const auth0Api = {
  /**
   * Get Auth0 user info for an X-Manager user
   */
  getAuth0User: async (userId: string) => {
    return apiClient.get<{ data: Auth0UserResponse }>(`/users/${userId}/auth0`)
  },

  /**
   * Get Auth0 admin dashboard URL for a user
   */
  getAuth0AdminUrl: async (userId: string) => {
    return apiClient.get<{ data: { url: string; auth0UserId: string } }>(
      `/users/${userId}/auth0/admin-url`
    )
  },

  /**
   * Create Auth0 user and link to existing X-Manager user
   */
  createAuth0User: async (userId: string, data: CreateAuth0UserRequest) => {
    return apiClient.post<{ data: Auth0UserResponse }>(`/users/${userId}/auth0`, data)
  },

  /**
   * Link user to existing Auth0 account
   */
  linkToAuth0: async (userId: string, data: LinkToAuth0Request) => {
    return apiClient.post<{ data: Auth0UserResponse }>(`/users/${userId}/auth0/link`, data)
  },

  /**
   * Unlink user from Auth0
   */
  unlinkFromAuth0: async (userId: string) => {
    return apiClient.delete(`/users/${userId}/auth0/link`)
  },

  /**
   * Sync user data with Auth0
   */
  syncWithAuth0: async (userId: string) => {
    return apiClient.post<{ data: Auth0UserResponse }>(`/users/${userId}/auth0/sync`)
  },

  /**
   * Get all available Auth0 connections
   */
  getConnections: async () => {
    return apiClient.get<{ data: Auth0ConnectionsResponse }>('/auth0/connections')
  },
}
